(() => {
  const SERVICE_VARIANT_ID = 47117362266166;
  const nativeFetch = window.fetch.bind(window);
  const state = {
    cart: null,
    analysis: null,
    refreshing: false,
    refreshQueued: false,
    refreshTimer: null,
    removingPair: false,
  };

  const rootRoute = () => window.Shopify?.routes?.root || '/';
  const cartEndpoint = (path) => `${rootRoute()}cart${path}`;
  const itemProperties = (item) => item?.properties || {};
  const itemQuantity = (item) => Math.max(1, Number(item?.quantity || 1));
  const isService = (item) =>
    Number(item?.variant_id ?? item?.id) === SERVICE_VARIANT_ID ||
    itemProperties(item)._by_alice_role === 'service';
  const isMadeToMeasureGarment = (item) => {
    const properties = itemProperties(item);
    return (
      properties._by_alice_role === 'garment' ||
      properties['Fit choice'] === 'Made to measure'
    );
  };

  const makePairKey = () =>
    window.crypto?.randomUUID?.() ||
    `ba-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const requestPath = (input) => {
    try {
      const value = typeof input === 'string' ? input : input?.url;
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return '';
    }
  };

  const isCartMutationPath = (path) =>
    /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(path);

  const linkMadeToMeasurePayload = (payload) => {
    if (!payload || !Array.isArray(payload.items)) return false;

    const garments = payload.items.filter(isMadeToMeasureGarment);
    const services = payload.items.filter(isService);
    if (!garments.length || !services.length) return false;

    const unusedServices = [...services];
    garments.forEach((garment) => {
      const quantity = itemQuantity(garment);
      let serviceIndex = unusedServices.findIndex((service) => itemQuantity(service) === quantity);
      if (serviceIndex < 0) serviceIndex = 0;
      const service = unusedServices.splice(serviceIndex, 1)[0];
      if (!service) return;

      const pairKey = makePairKey();
      garment.quantity = quantity;
      garment.properties = {
        ...itemProperties(garment),
        _by_alice_pair: pairKey,
        _by_alice_role: 'garment',
      };

      service.quantity = quantity;
      service.properties = {
        ...itemProperties(service),
        _by_alice_pair: pairKey,
        _by_alice_role: 'service',
      };
    });

    return true;
  };

  window.fetch = (input, init) => {
    const path = requestPath(input);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    let nextInit = init;

    if (method === 'POST' && /\/cart\/add(\.js)?\/?$/.test(path) && typeof init?.body === 'string') {
      try {
        const payload = JSON.parse(init.body);
        if (linkMadeToMeasurePayload(payload)) {
          nextInit = { ...init, body: JSON.stringify(payload) };
        }
      } catch (_error) {
        // Leave non-JSON cart requests untouched.
      }
    }

    const responsePromise = nativeFetch(input, nextInit);
    if (isCartMutationPath(path)) {
      responsePromise
        .then((response) => {
          if (response.ok) scheduleRefresh(180);
        })
        .catch(() => {});
    }
    return responsePromise;
  };

  const analyseCart = (cart) => {
    const explicitGroups = new Map();
    const groups = new Map();
    const relevantItems = [];
    const orphanGarments = [];
    const orphanServices = [];
    const issues = [];

    for (const item of cart?.items || []) {
      if (!isService(item) && !isMadeToMeasureGarment(item)) continue;
      relevantItems.push(item);

      const pairKey = itemProperties(item)._by_alice_pair;
      if (!pairKey) {
        if (isService(item)) orphanServices.push(item);
        else orphanGarments.push(item);
        continue;
      }

      if (!explicitGroups.has(pairKey)) {
        explicitGroups.set(pairKey, { garments: [], services: [] });
      }
      const group = explicitGroups.get(pairKey);
      if (isService(item)) group.services.push(item);
      else group.garments.push(item);
    }

    for (const [pairKey, group] of explicitGroups) {
      const complete =
        group.garments.length === 1 &&
        group.services.length === 1 &&
        itemQuantity(group.garments[0]) === itemQuantity(group.services[0]);

      if (complete) {
        groups.set(pairKey, group);
      } else {
        orphanGarments.push(...group.garments);
        orphanServices.push(...group.services);
      }
    }

    const availableServices = [...orphanServices];
    orphanGarments.forEach((garment, index) => {
      const quantity = itemQuantity(garment);
      const serviceIndex = availableServices.findIndex(
        (service) => itemQuantity(service) === quantity
      );

      if (serviceIndex < 0) {
        issues.push('A made-to-measure garment does not have a matching $20 service line.');
        return;
      }

      const service = availableServices.splice(serviceIndex, 1)[0];
      const legacyKey = `legacy-${index}-${garment.key || index}`;
      groups.set(legacyKey, { garments: [garment], services: [service] });
    });

    if (availableServices.length) {
      issues.push('A made-to-measure service line does not have a matching garment.');
    }

    return {
      valid: issues.length === 0,
      issues,
      groups,
      relevantItems,
      hasMadeToMeasure: relevantItems.length > 0,
    };
  };

  const rowForKey = (key) =>
    [...document.querySelectorAll('[data-key]')].find((row) => row.dataset.key === key) || null;

  const resetCartRows = () => {
    document.querySelectorAll('[data-ba-linked-row]').forEach((row) => {
      row.removeAttribute('data-ba-linked-row');
      row.removeAttribute('data-ba-pair-key');
      row.classList.remove('ba-linked-service', 'ba-linked-garment');
    });
    document.querySelectorAll('[data-ba-linked-note], [data-ba-fixed-quantity]').forEach((node) => node.remove());
    document.querySelectorAll('[data-ba-remove-pair]').forEach((button) => button.removeAttribute('data-ba-remove-pair'));
  };

  const addLinkedNote = (row, text) => {
    const host = row.querySelector('.cart-items__variants-wrapper, .cart-items__details');
    if (!host || host.querySelector('[data-ba-linked-note]')) return;
    const note = document.createElement('p');
    note.className = 'ba-cart-linked-note';
    note.dataset.baLinkedNote = 'true';
    note.textContent = text;
    host.append(note);
  };

  const addFixedQuantity = (row, quantity) => {
    const host = row.querySelector('.cart-items__quantity-controls, .cart-items__quantity');
    if (!host || host.querySelector('[data-ba-fixed-quantity]')) return;
    const label = document.createElement('span');
    label.className = 'ba-cart-fixed-quantity';
    label.dataset.baFixedQuantity = 'true';
    label.textContent = `Qty ${quantity}`;
    host.prepend(label);
  };

  const decoratePair = (pairKey, group) => {
    const garment = group.garments[0];
    const service = group.services[0];
    if (!garment || !service) return;

    const garmentRow = rowForKey(garment.key);
    if (garmentRow) {
      garmentRow.dataset.baLinkedRow = 'garment';
      garmentRow.dataset.baPairKey = pairKey;
      garmentRow.classList.add('ba-linked-garment');
      addLinkedNote(garmentRow, 'Made to measure · linked $20 service included below');
      addFixedQuantity(garmentRow, garment.quantity);
      const removeButton = garmentRow.querySelector('.cart-items__remove');
      if (removeButton) removeButton.dataset.baRemovePair = pairKey;
    }

    const serviceRow = rowForKey(service.key);
    if (serviceRow) {
      serviceRow.dataset.baLinkedRow = 'service';
      serviceRow.dataset.baPairKey = pairKey;
      serviceRow.classList.add('ba-linked-service');
      addLinkedNote(serviceRow, 'Linked to the made-to-measure garment above');
      addFixedQuantity(serviceRow, service.quantity);
    }
  };

  const setCheckoutState = (analysis) => {
    const shouldBlock = analysis.hasMadeToMeasure && !analysis.valid;
    const buttons = document.querySelectorAll('#checkout, button[name="checkout"], input[name="checkout"]');

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;
      if (shouldBlock) {
        if (!button.disabled) button.dataset.baGuardDisabled = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.baGuardDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.baGuardDisabled;
      }
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      container.toggleAttribute('data-ba-checkout-blocked', shouldBlock);
    });

    document.querySelectorAll('[data-ba-cart-warning]').forEach((warning) => warning.remove());
    if (!shouldBlock) return;

    document.querySelectorAll('.cart__ctas, .cart-summary__checkout').forEach((ctas) => {
      if (ctas.parentElement?.querySelector('[data-ba-cart-warning]')) return;
      const warning = document.createElement('div');
      warning.className = 'ba-cart-warning';
      warning.dataset.baCartWarning = 'true';
      warning.setAttribute('role', 'alert');
      warning.innerHTML = '<strong>Made-to-measure items need fixing</strong><span>The garment and its $20 service do not match. Remove the garment and add it again before checkout.</span>';
      ctas.before(warning);
    });
  };

  const applyCartGuard = (cart) => {
    const analysis = analyseCart(cart);
    state.cart = cart;
    state.analysis = analysis;

    resetCartRows();
    for (const [pairKey, group] of analysis.groups) {
      decoratePair(pairKey, group);
    }
    setCheckoutState(analysis);
  };

  const refreshCart = async () => {
    if (state.refreshing) {
      state.refreshQueued = true;
      return;
    }

    state.refreshing = true;
    try {
      const response = await nativeFetch(cartEndpoint('.js'), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Cart could not be checked.');
      applyCartGuard(await response.json());
    } catch (error) {
      console.warn('[By Alice] Cart safeguard could not refresh.', error);
    } finally {
      state.refreshing = false;
      if (state.refreshQueued) {
        state.refreshQueued = false;
        scheduleRefresh(120);
      }
    }
  };

  function scheduleRefresh(delay = 120) {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(refreshCart, delay);
  }

  const removeLinkedPair = async (pairKey, trigger) => {
    if (state.removingPair || !state.analysis) return;
    const group = state.analysis.groups.get(pairKey);
    if (!group) return;

    const updates = {};
    [...group.garments, ...group.services].forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return;

    state.removingPair = true;
    trigger?.setAttribute('aria-busy', 'true');
    trigger?.setAttribute('disabled', '');

    try {
      const response = await nativeFetch(cartEndpoint('/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('The linked items could not be removed.');
      window.location.reload();
    } catch (error) {
      console.error('[By Alice] Linked cart removal failed.', error);
      state.removingPair = false;
      trigger?.removeAttribute('aria-busy');
      trigger?.removeAttribute('disabled');
      window.alert('The linked made-to-measure items could not be removed. Please refresh and try again.');
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const removeButton = target?.closest('[data-ba-remove-pair]');
      if (removeButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        removeLinkedPair(removeButton.dataset.baRemovePair, removeButton);
        return;
      }

      const checkoutTarget = target?.closest('#checkout, [name="checkout"], .additional-checkout-buttons');
      if (checkoutTarget && state.analysis?.hasMadeToMeasure && !state.analysis.valid) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setCheckoutState(state.analysis);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const isCheckout =
        submitter?.id === 'checkout' || submitter?.getAttribute('name') === 'checkout';
      if (isCheckout && state.analysis?.hasMadeToMeasure && !state.analysis.valid) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setCheckoutState(state.analysis);
      }
    },
    true
  );

  document.addEventListener('cart:update', () => scheduleRefresh(220));
  document.addEventListener('cart:add', () => scheduleRefresh(220));
  document.addEventListener('DOMContentLoaded', () => scheduleRefresh(0), { once: true });
  if (document.readyState !== 'loading') scheduleRefresh(0);

  const style = document.createElement('style');
  style.textContent = `
    .ba-linked-service .quantity-selector,
    .ba-linked-service .cart-items__remove,
    .ba-linked-garment .quantity-selector {
      display: none !important;
    }

    .ba-cart-fixed-quantity {
      display: inline-flex;
      min-height: 38px;
      align-items: center;
      padding-inline: 12px;
      border: 1px solid rgb(var(--color-border-rgb) / 0.7);
      border-radius: var(--style-border-radius-inputs, 10px);
      font-size: var(--font-size--sm, 0.875rem);
      white-space: nowrap;
    }

    .ba-cart-linked-note {
      margin-block-start: 7px !important;
      color: var(--ba-rose, #ce5f70);
      font-size: var(--font-size--xs, 0.75rem) !important;
      font-weight: 650;
      line-height: 1.4;
    }

    .ba-linked-service {
      border: 1px solid rgb(var(--color-border-rgb) / 0.75);
      border-radius: 16px;
      padding: 14px;
      background: rgb(var(--color-background-rgb) / 0.55);
    }

    .ba-cart-warning {
      display: grid;
      gap: 4px;
      margin-block-end: 14px;
      padding: 14px 16px;
      border: 1px solid rgba(160, 36, 50, 0.28);
      border-radius: 14px;
      background: rgba(206, 95, 112, 0.12);
      color: #8d2637;
      line-height: 1.45;
    }

    .ba-cart-warning span {
      font-size: 0.875rem;
    }

    .additional-checkout-buttons[data-ba-checkout-blocked] {
      display: none !important;
    }
  `;
  document.head.append(style);
})();
